import React, { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { FieldDto, TruliooFieldDto } from 'api/Clients/Types/TruliooFieldDto'
import { TruliooVerification } from 'api/Clients/Types/TruliooVerification'
import { Spinner } from 'components/Spinner'
import { ErrorMessage, Field, Form, Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik'
import styled from 'styled-components'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useFetchOne } from 'utils/api/useFetch'

import './ClientDetailsTruliooWindow.scss'

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 50%;

  p {
    font-size: 16px;
    background-color: #d9edf7;
  }

  button {
    display: flex;
    justify-content: center;
  }
`

const useForm = (
  props: ClientDetailsTruliooWindowProps & { fields: TruliooFieldDto }
): FormikConfig<TruliooFieldDto> => {
  return {
    initialValues: props.fields,
    enableReinitialize: true,
    onSubmit: async (
      values: TruliooFieldDto,
      actions: FormikHelpers<TruliooFieldDto>
    ): Promise<void> => {
      actions.setSubmitting(true)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const personInfo: Record<string, any> = { additionalFields: {} }
      ;[...(values.personInfo?.fields || [])].forEach((field) => {
        personInfo[field.name] = field.value ?? null
      })
      ;[...(values.personInfo?.additionalFields?.fields || [])].forEach((field) => {
        personInfo.additionalFields[field.name] = field.value ?? null
      })
      const communication: Record<string, string | number | null | boolean> = {}
      ;[
        ...(values.communication?.fields || []),
        ...(values.communication?.additionalFields?.fields || []),
      ].forEach((field) => {
        communication[field.name] = field.value ?? null
      })
      const driverLicence: Record<string, string | number | null | boolean> = {}
      ;[
        ...(values.driverLicence?.fields || []),
        ...(values.driverLicence?.additionalFields?.fields || []),
      ].forEach((field) => {
        driverLicence[field.name] = field.value ?? null
      })
      const passport: Record<string, string | number | null | boolean> = {}
      ;[
        ...(values.passport?.fields || []),
        ...(values.passport?.additionalFields?.fields || []),
      ].forEach((field) => {
        passport[field.name] = field.value ?? null
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const location: Record<string, any> = { additionalFields: {} }
      ;[...(values.location?.fields || [])].forEach((field) => {
        location[field.name] = field.value ?? null
      })
      ;[...(values.location?.additionalFields?.fields || [])].forEach((field) => {
        location.additionalFields[field.name] = field.value ?? null
      })
      const data: TruliooVerification = {
        fields: {
          personInfo,
          communication,
          driverLicence,
          passport,
          location,
          nationalIds: [
            {
              number:
                (values.nationalIds &&
                  ((values.nationalIds[0].fields || []).find((field) => field.name === 'Number')
                    ?.value as string)) ||
                null,
              type:
                (values.nationalIds &&
                  ((values.nationalIds[0].fields || []).find((field) => field.name === 'Type')
                    ?.value as string)) ||
                null,
              cityOfIssue:
                (values.nationalIds &&
                  ((values.nationalIds[0].fields || []).find(
                    (field) => field.name === 'CityOfIssue'
                  )?.value as string)) ||
                null,
              countyOfIssue:
                (values.nationalIds &&
                  ((values.nationalIds[0].fields || []).find(
                    (field) => field.name === 'CountyOfIssue'
                  )?.value as string)) ||
                null,
              districtOfIssue:
                (values.nationalIds &&
                  ((values.nationalIds[0].fields || []).find(
                    (field) => field.name === 'DistrictOfIssue'
                  )?.value as string)) ||
                null,
              provinceOfIssue:
                (values.nationalIds &&
                  ((values.nationalIds[0].fields || []).find(
                    (field) => field.name === 'ProvinceOfIssue'
                  )?.value as string)) ||
                null,
            },
          ],
          countrySpecificFields: values.countrySpecificFields
            ? values.countrySpecificFields.fields.reduce(
                (previousValue, currentValue) => ({
                  ...previousValue,
                  [currentValue.name]: (currentValue.value as string) || null,
                }),
                {} as { [key: string]: string | null }
              )
            : {},
        },
      }

      try {
        await props.onSubmit(data)
        actions.setSubmitting(false)
        props.closeModal()
      } catch (error) {
        console.error(error)
        actions.setSubmitting(false)
      }
    },
  }
}

interface ClientDetailsTruliooWindowProps {
  clientId: string
  onSubmit: (data: TruliooVerification) => void
  closeModal: () => void
}

interface Props {
  label: string
  fields: FieldDto[]
  additionalFields?: { fields: FieldDto[] } | null
  namePrefix: string
  additionalFieldsPrefix?: string

  setFieldValue(field: string, value: FieldDto & { value: string }): void
}

const FieldsSection = ({
  label,
  fields,
  additionalFields,
  namePrefix,
  setFieldValue,
  additionalFieldsPrefix,
}: Props) => {
  if (!fields.length) {
    return null
  }

  return (
    <section className='trulioo-window'>
      <label>{label}</label>
      {fields.map((field, i) => (
        <div key={field.name}>
          <span>
            {field.label || field.name}
            <span style={{ color: 'red' }}>{field.required ? '*' : null}</span>
          </span>
          <div className='trulioo-window-input-wrapper'>
            <Field
              required={field.required}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                const f = fields.find((x) => x.name === field.name)
                if (f) {
                  setFieldValue(`${namePrefix}[${i}]`, Object.assign(f, { value }))
                }
              }}
              value={field.value}
              type={field.type === 'int' ? 'number' : 'text'}
              touched={true}
              placeholder={field.description}
            />
            <ErrorMessage
              name={`${namePrefix}[${i}].value`}
              component='div'
              className='transaction__invalid-feedback'
            />
          </div>
        </div>
      ))}
      {(additionalFields?.fields || []).map((field, i) => (
        <div key={field.name}>
          <span>
            {field.label || field.name}
            <span style={{ color: 'red' }}>{field.required ? '*' : null}</span>
          </span>
          <div className='trulioo-window-input-wrapper'>
            <Field
              required={field.required}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                const f = additionalFields?.fields.find((x) => x.name === field.name)
                if (f) {
                  setFieldValue(`${additionalFieldsPrefix}[${i}]`, Object.assign(f, { value }))
                }
              }}
              value={field.value}
              type={field.type === 'int' ? 'number' : 'text'}
              touched={true}
              placeholder={field.description}
            />
            <ErrorMessage
              name={`${namePrefix}[${i}].value`}
              component='div'
              className='transaction__invalid-feedback'
            />
          </div>
        </div>
      ))}
    </section>
  )
}

const ClientDetailsTruliooWindow: React.FC<ClientDetailsTruliooWindowProps> = (props) => {
  const clientsApiClient = useApiClient(ClientsApiClient)

  const callback = useCallback(
    () => clientsApiClient.getTruliooFields(props.clientId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.clientId]
  )
  const { data: fields, isLoading } = useFetchOne<TruliooFieldDto>(callback)
  const form = useForm({ ...props, fields: fields || ({} as TruliooFieldDto) })

  if (isLoading) {
    return <Spinner />
  }

  if (!fields) {
    return (
      <FieldsWrapper>
        <p>No fields data for identity verification.</p>
        <Button type='button' onClick={props.closeModal}>
          Close
        </Button>
      </FieldsWrapper>
    )
  }

  return (
    <Formik {...form}>
      {({ isValid, isSubmitting, setFieldValue }: FormikProps<TruliooFieldDto>) => {
        return (
          <Form className='trulioo-window'>
            <FieldsSection
              label='Personal Information'
              fields={fields.personInfo?.fields || []}
              additionalFields={fields?.personInfo?.additionalFields}
              namePrefix='personInfo.fields'
              additionalFieldsPrefix='personInfo.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Location'
              fields={fields.location?.fields || []}
              additionalFields={fields?.location?.additionalFields}
              namePrefix='location.fields'
              additionalFieldsPrefix='location.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Communication'
              fields={fields.communication?.fields || []}
              additionalFields={fields?.communication?.additionalFields}
              namePrefix='communication.fields'
              additionalFieldsPrefix='communication.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Driver License'
              fields={fields.driverLicence?.fields || []}
              additionalFields={fields?.driverLicence?.additionalFields}
              namePrefix='driverLicence.fields'
              additionalFieldsPrefix='driverLicence.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='National IDs'
              fields={fields?.nationalIds?.length ? fields.nationalIds[0]?.fields : []}
              additionalFields={
                fields?.nationalIds?.length ? fields?.nationalIds[0]?.additionalFields : undefined
              }
              namePrefix='nationalIds[0].fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Driver License'
              fields={fields.passport?.fields || []}
              additionalFields={fields?.passport?.additionalFields}
              namePrefix='passport.fields'
              additionalFieldsPrefix='passport.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Country Specific Fields'
              fields={fields.countrySpecificFields?.fields || []}
              additionalFields={fields?.countrySpecificFields?.additionalFields}
              namePrefix='countrySpecificFields.fields'
              additionalFieldsPrefix='countrySpecificFields.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Document'
              fields={fields.document?.fields || []}
              additionalFields={fields?.document?.additionalFields}
              namePrefix='document.fields'
              additionalFieldsPrefix='document.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <FieldsSection
              label='Business'
              fields={fields.business?.fields || []}
              additionalFields={fields?.business?.additionalFields}
              namePrefix='business.fields'
              additionalFieldsPrefix='business.additionalFields.fields'
              setFieldValue={setFieldValue}
            />
            <section className='trulioo-window-submit'>
              <Button disabled={!isValid || isSubmitting} type='submit'>
                Submit
              </Button>
            </section>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ClientDetailsTruliooWindow
